import {useFlags, useLDClient} from 'launchdarkly-react-client-sdk';
import {useState, useEffect} from 'react';
import getCookieValue from '@/utils/getCookieValue';
import {getEnv} from '@/config/environment';
import {DashboardUser} from '@/types/dashboardUser';
import useAgencyQuery from '@/hooks/queries/use-agency-query';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useToken from '@/hooks/use-token';
import useBrandId from '../use-brand-id';

const LT_ID_COOKIE_NAME = getEnv().MODE === 'staging' ? 'stg__lt_id' : 'lt_id';

function useSetLaunchDarklyContext() {
  const {isAuthenticated, initialized: tokenInitialized} = useToken(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const flags = useFlags();
  const activeBrandId = useBrandId();
  const ldClient = useLDClient();
  const ltId = getCookieValue(LT_ID_COOKIE_NAME);

  const {data: agency} = useAgencyQuery({brandId: activeBrandId});

  const {dashboardUser}: {dashboardUser: DashboardUser} = useDashboardUserQuery(
    {
      overrideEnabled: isAuthenticated,
      include: ['agency'],
    }
  );

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    // Handle anonymous user identification
    if (!isAuthenticated && ldClient?.getContext()?.key !== 'anonymous-user') {
      ldClient.identify({key: 'anonymous-user', anonymous: true});
    }

    // Handle authenticated user identification (initial or activeBrandId update)
    if (isAuthenticated && tokenInitialized && dashboardUser?.email && ltId) {
      const currentContext = ldClient.getContext() as Record<string, unknown>;
      const agencyId = dashboardUser?.agency?.id;
      const activeAgencyId = agency?.id;

      // Initial identification: context key doesn't match
      const needsInitialIdentification = currentContext?.key !== ltId;

      // activeBrandId update: already identified but activeBrandId changed
      const needsBrandIdUpdate =
        isInitialized &&
        activeBrandId &&
        currentContext?.active_brand_id !== activeBrandId;

      // AgencyId update: already identified but agencyId changed
      const needsAgencyIdUpdate =
        isInitialized &&
        activeAgencyId &&
        currentContext?.active_agency_id !== activeAgencyId;

      if (
        needsInitialIdentification ||
        needsBrandIdUpdate ||
        needsAgencyIdUpdate
      ) {
        ldClient
          .identify({
            key: ltId,
            kind: 'user',
            name: dashboardUser?.displayName,
            first_name: dashboardUser?.firstName,
            last_name: dashboardUser?.lastName,
            email: dashboardUser?.email,
            ip: dashboardUser?.currentSignInIp,
            // Comes from dashboardUser.currentBrandId; reflects the actual
            // brand of the logged-in user.
            brand_id: dashboardUser?.currentBrandId,
            // Comes from the URL (e.g. /?brandId=brandId&...). Does not reflect the actual
            // brand of the logged-in user when they are an admin or have multiple brands.
            active_brand_id: activeBrandId,
            // Comes from dashboardUser.agency; reflects the agency the user belongs to regardless of the active brand.
            agency_id: agencyId,
            // Comes from useAgencyQuery; reflects the agency of the active brand. Will be different from agency_id when user is part of an agency that has multiple brands.
            active_agency_id: activeAgencyId,
          })
          .then(() => {
            setIsInitialized(true);
          })
          .catch((e) => {
            console.error('LaunchDarkly identify error:', e);
            setIsInitialized(true);
          });
      }
    }

    // Set initialized state for unauthenticated users
    if (tokenInitialized && !isAuthenticated && !isInitialized) {
      setIsInitialized(true);
    }
  }, [
    isAuthenticated,
    tokenInitialized,
    dashboardUser,
    isInitialized,
    ltId,
    ldClient,
    activeBrandId,
    agency?.id,
  ]);
  return {
    flags,
    launchDarklyInitialized: isInitialized,
  };
}

export default useSetLaunchDarklyContext;
